import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Eye,
  EyeOff,
  Lock,
  Mail,
} from 'lucide-react'
import { AuthLayout } from '../../components/layout/AuthLayout'
import { api, extractApiError } from '../../lib/api'
import {
  confirmPasswordRefine,
  passwordStrongSchema,
} from '../../lib/validators'

/**
 * Join Company (Phase 2) — accept a company invitation via the single-use
 * link from the email. The backend tells us whether this is a brand-new
 * person who must SET a password (`mode: 'set'`) or an existing person who
 * confirms their CURRENT password (`mode: 'signin'`). On success the server
 * signs them in, so we hard-navigate to the app root and let the role
 * landing take over.
 */
export function JoinPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  // 'loading' → fetching invite info; 'invalid' → bad/used/expired token;
  // otherwise the resolved info object.
  const [state, setState] = useState(token ? 'loading' : 'invalid')

  useEffect(() => {
    if (!token) return
    let cancelled = false
    api
      .get('/auth/join/info', { params: { token } })
      .then((res) => {
        if (cancelled) return
        setState(res.data?.valid ? res.data : 'invalid')
      })
      .catch(() => {
        if (!cancelled) setState('invalid')
      })
    return () => {
      cancelled = true
    }
  }, [token])

  if (state === 'invalid') {
    return (
      <AuthLayout
        centered
        title="Invitation Link Invalid"
        subtitle="This invitation link is invalid, has already been used, or has expired."
      >
        <div className="rounded-xl border border-[color:var(--color-danger-border)] bg-[color:var(--color-danger-bg)] p-4 text-sm text-[color:var(--color-ink)] flex items-start gap-3">
          <AlertTriangle
            size={18}
            className="text-[color:var(--color-danger)] shrink-0 mt-0.5"
          />
          <p className="m-0">
            Ask your administrator to resend the invitation, then open the
            newest link.
          </p>
        </div>
        <p className="mt-6 text-center text-sm text-[color:var(--color-ink-muted)]">
          <Link
            to="/sign-in"
            className="font-semibold text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-hover)]"
          >
            Back to Sign In
          </Link>
        </p>
      </AuthLayout>
    )
  }

  if (state === 'loading') {
    return (
      <AuthLayout centered title="Join Company" subtitle="Checking your invitation…">
        <div className="flex justify-center py-6">
          <span className="inline-block w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full animate-spin" />
        </div>
      </AuthLayout>
    )
  }

  const isSet = state.mode === 'set'
  return (
    <AuthLayout
      centered
      title={`Join ${state.companyName || 'the company'}`}
      subtitle={
        isSet
          ? 'Set a password to finish creating your account and join.'
          : 'Confirm your existing password to join this company.'
      }
    >
      <div className="rounded-lg border border-[color:var(--color-rule)] p-4 bg-[color:var(--color-canvas)] space-y-2 mb-5">
        <div className="flex items-center justify-between text-sm gap-3">
          <span className="font-semibold text-[color:var(--color-ink-strong)] flex items-center gap-1.5">
            <Mail size={13} /> Email ID
          </span>
          <span className="text-[color:var(--color-ink)] truncate">
            {state.email || '—'}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm gap-3">
          <span className="font-semibold text-[color:var(--color-ink-strong)] flex items-center gap-1.5">
            <Building2 size={13} /> Company
          </span>
          <span className="text-[color:var(--color-ink)] truncate">
            {state.companyName || '—'}
          </span>
        </div>
      </div>

      <JoinForm
        token={token}
        mode={state.mode}
        companyName={state.companyName}
      />
    </AuthLayout>
  )
}

function JoinForm({ token, mode, companyName }) {
  const isSet = mode === 'set'
  const [showPw, setShowPw] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const schema = isSet
    ? z
        .object({
          password: passwordStrongSchema,
          confirmPassword: z.string({ message: 'Confirm Password is required.' }),
        })
        .superRefine(confirmPasswordRefine('password', 'confirmPassword'))
    : z.object({
        password: z
          .string({ message: 'Password is required.' })
          .min(1, 'Password is required.'),
      })

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: isSet
      ? { password: '', confirmPassword: '' }
      : { password: '' },
  })

  async function onSubmit(values) {
    try {
      await api.post('/auth/join/accept', { token, password: values.password })
      toast.success(`You've joined ${companyName || 'the company'}.`)
      // The server set the session cookies — hard-navigate so bootstrap
      // re-reads /auth/me and the role landing takes over.
      window.location.assign('/')
    } catch (err) {
      const { message, fields } = extractApiError(err)
      if (fields?.token) {
        toast.error(fields.token)
      } else if (fields?.password) {
        form.setError('password', { message: fields.password })
      } else {
        toast.error(message)
      }
    }
  }

  const passwordError = form.formState.errors.password?.message
  const confirmError = form.formState.errors.confirmPassword?.message
  const submitting = form.formState.isSubmitting

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-5">
      <PasswordInput
        id="password"
        label={isSet ? 'Set Password' : 'Password'}
        required
        autoComplete={isSet ? 'new-password' : 'current-password'}
        placeholder={isSet ? 'Choose a password' : 'Enter your password'}
        visible={showPw}
        onToggleVisible={() => setShowPw((v) => !v)}
        error={passwordError}
        register={form.register('password')}
      />
      {isSet ? (
        <PasswordInput
          id="confirmPassword"
          label="Confirm Password"
          required
          autoComplete="new-password"
          placeholder="Re-enter Password"
          visible={showConfirm}
          onToggleVisible={() => setShowConfirm((v) => !v)}
          error={confirmError}
          register={form.register('confirmPassword')}
        />
      ) : null}

      <button
        type="submit"
        disabled={submitting}
        className="group w-full h-12 rounded-xl text-white font-semibold inline-flex items-center justify-center gap-2 shadow-[0_8px_20px_-6px_rgba(2,54,123,0.35)] hover:shadow-[0_12px_28px_-6px_rgba(2,54,123,0.45)] transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
        style={{
          background:
            'linear-gradient(135deg, #02367B 0%, #006CA5 50%, #0496C7 100%)',
        }}
      >
        {submitting ? (
          <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : null}
        {isSet ? 'Set Password & Join' : 'Confirm & Join'}
        <ArrowRight
          size={16}
          className="transition-transform group-hover:translate-x-0.5"
        />
      </button>

      <p className="text-center text-sm text-[color:var(--color-ink-muted)]">
        Not you?{' '}
        <Link
          to="/sign-in"
          className="font-semibold text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-hover)]"
        >
          Back to Sign In
        </Link>
      </p>
    </form>
  )
}

/** Lock-prefix password input with eye toggle — same visual contract as
 *  the Sign In / Reset Password forms. */
function PasswordInput({
  id,
  label,
  required,
  autoComplete,
  placeholder,
  visible,
  onToggleVisible,
  error,
  register,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block w-fit mb-1.5 text-[14px] font-medium text-[color:var(--color-ink-strong)]"
      >
        {label}
        {required ? (
          <span
            aria-hidden="true"
            className="ml-0.5 text-[color:var(--color-priority-urgent)]"
          >
            *
          </span>
        ) : null}
      </label>
      <div className="relative">
        <Lock
          size={16}
          aria-hidden="true"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
        />
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          placeholder={placeholder}
          maxLength={128}
          className={
            'w-full h-11 pl-10 pr-10 rounded-xl border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-primary)] ' +
            (error
              ? 'border-[color:var(--color-priority-urgent)]'
              : 'border-[color:var(--color-rule)]')
          }
          {...register}
        />
        <button
          type="button"
          onClick={onToggleVisible}
          aria-label={visible ? 'Hide password' : 'Show password'}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          {visible ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
      </div>
      {error ? (
        <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
          {error}
        </p>
      ) : null}
    </div>
  )
}
